import { useEffect, useRef, useState } from "react";
import { InvoiceTemplate } from "@/components/dashboard/invoices/InvoiceTemplate";
import type { Invoice, InvoiceTemplate as TemplateId } from "@/types/invoice";

const DESIGN_WIDTH = 800;

interface BusinessInfo {
  name: string;
  logo?: string;
  address?: string;
  email?: string;
  phone?: string;
  website?: string;
  currency?: string;
}

interface InvoiceTemplatePreviewProps {
  invoice: Invoice;
  business: BusinessInfo;
  className?: string;
}

export function buildSampleInvoice(options: {
  templateId: TemplateId;
  prefix?: string;
  taxRate?: number;
  paymentTerms?: number;
  notes?: string;
  terms?: string;
  currency?: string;
}): Invoice {
  const issueDate = new Date();
  const dueDate = new Date(issueDate);
  dueDate.setDate(dueDate.getDate() + (options.paymentTerms || 14));

  const items = [
    {
      _id: "sample-1",
      itemName: "Premium Cotton Tee",
      description: "Size M · Black",
      unitPrice: 15000,
      quantity: 2,
      discount: 0,
    },
    {
      _id: "sample-2",
      itemName: "Canvas Tote Bag",
      description: "Natural",
      unitPrice: 8500,
      quantity: 1,
      discount: 10,
    },
  ];

  const subTotal = items.reduce((sum, item) => sum + item.unitPrice * item.quantity, 0);
  const discountTotal = items.reduce(
    (sum, item) => sum + item.unitPrice * item.quantity * ((item.discount || 0) / 100),
    0,
  );
  const taxable = subTotal - discountTotal;
  const taxRate = Number(options.taxRate) || 0;
  const taxAmount = taxable * (taxRate / 100);
  const total = taxable + taxAmount;
  const prefix = (options.prefix || "INV").trim().toUpperCase() || "INV";

  return {
    _id: "sample",
    storeId: "sample",
    createdBy: "sample",
    invoiceNumber: `${prefix}-0042`,
    type: "standard",
    status: "sent",
    client: {
      name: "Adaeze Okonkwo",
      email: "adaeze@example.com",
      phone: "+234 801 234 5678",
      address: "14 Admiralty Way, Lekki, Lagos",
    },
    items,
    currency: options.currency || "NGN",
    subTotal,
    discountTotal,
    taxRate,
    taxAmount,
    total,
    notes: options.notes || "Thank you for your business.",
    terms: options.terms || "Payment is due within the stated payment terms.",
    dueDate: dueDate.toISOString(),
    issueDate: issueDate.toISOString(),
    totalAmountReceived: 0,
    paymentRecords: [],
    templateId: options.templateId,
    createdAt: issueDate.toISOString(),
    updatedAt: issueDate.toISOString(),
  };
}

export function InvoiceTemplatePreview({
  invoice,
  business,
  className,
}: InvoiceTemplatePreviewProps) {
  const outerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [height, setHeight] = useState(480);

  useEffect(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner) return;

    const update = () => {
      const nextScale = Math.min(1, outer.clientWidth / DESIGN_WIDTH);
      setScale(nextScale);
      setHeight(inner.scrollHeight * nextScale);
    };

    const observer = new ResizeObserver(update);
    observer.observe(outer);
    observer.observe(inner);
    const images = Array.from(inner.querySelectorAll("img"));
    images.forEach((image) => {
      if (!image.complete) image.addEventListener("load", update);
    });
    update();
    return () => {
      observer.disconnect();
      images.forEach((image) => image.removeEventListener("load", update));
    };
  }, [invoice, business]);

  return (
    <div ref={outerRef} className={className}>
      <div className="overflow-hidden" style={{ height }}>
        <div
          ref={innerRef}
          className="light-surface pointer-events-none origin-top-left bg-white text-[#292d32]"
          style={{
            width: DESIGN_WIDTH,
            transform: `scale(${scale})`,
          }}
        >
          <InvoiceTemplate key={invoice.templateId} invoice={invoice} business={business} />
        </div>
      </div>
    </div>
  );
}
